import { css } from '@firebolt-dev/css'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Interfaces } from '../../shared/interfaces'
import { items } from '../../shared/Items'
import { InterfaceCell } from './InterfaceCell'
import { InterfaceExtra } from './InterfaceExtra'
import { TOOLTIP_DELAY } from './ItemTooltip'

export function InterfaceView({
  world,
  kind,
  name,
  localInv,
  targetInv,
  recipes,
  activeSlot,
  resonance,
  onMove,
  onSelect,
  onGoHotbar,
  onGoEquipment,
  onGoChat,
  onTakeAll,
  onCraft,
  onCloseChat,
}) {
  const ui = Interfaces[kind]
  const [src, setSrc] = useState(null)
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

  const containerRef = useRef(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    // custom drag cursor
    document.body.classList.toggle('dragging', !!src?.moving)
    return () => document.body.classList.remove('dragging')
  }, [src?.moving])

  useLayoutEffect(() => {
    if (!containerRef.current) return
    const { width, height } = containerRef.current.getBoundingClientRect()
    if (width && height) setScale(Math.min(width / ui.width, height / ui.height))
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setScale(Math.min(width / ui.width, height / ui.height))
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [ui.width, ui.height])

  const handlePointerDown = (type, idx, cell, slot) => e => {
    e.currentTarget.closest('.interface').setPointerCapture(e.pointerId)
    const rect = e.currentTarget.closest('.interface-inner').getBoundingClientRect()
    const rectScale = rect.width / ui.width
    const x = (e.clientX - rect.left) / rectScale
    const y = (e.clientY - rect.top) / rectScale
    setCursorPos({ x, y })
    setSrc({ type, idx, cell, slot, moving: false, sx: x, sy: y, downAt: Date.now() })
  }

  const handlePointerMove = e => {
    if (!src) return
    const rect = e.currentTarget.querySelector('.interface-inner').getBoundingClientRect()
    const x = (e.clientX - rect.left) / scale
    const y = (e.clientY - rect.top) / scale

    setCursorPos({ x, y })
    if (!src.slot || src.moving) return
    const distX = Math.abs(x - src.sx)
    const distY = Math.abs(y - src.sy)
    if (distX > 4 || distY > 4) {
      setSrc({ ...src, moving: true })
    }
  }

  const handlePointerUp = e => {
    if (!src) return
    const rect = e.currentTarget.querySelector('.interface-inner').getBoundingClientRect()
    const x = (e.clientX - rect.left) / scale
    const y = (e.clientY - rect.top) / scale

    let dest = null
    for (const cell of ui.localCells) {
      if (x >= cell.x && x < cell.x + 20 && y >= cell.y && y < cell.y + 20) {
        dest = { type: 'local', idx: cell.idx }
        break
      }
    }
    if (!dest) {
      for (const cell of ui.targetCells) {
        if (x >= cell.x && x < cell.x + 20 && y >= cell.y && y < cell.y + 20) {
          dest = { type: 'target', idx: cell.idx }
          break
        }
      }
    }
    if (dest) {
      const isLongPress = Date.now() - src.downAt >= TOOLTIP_DELAY
      if (src.cell.select && src.type === 'local' && dest.type === 'local' && src.idx === dest.idx) {
        if (!isLongPress) onSelect?.(dest.idx)
      } else {
        onMove({
          srcType: src.type,
          srcIdx: src.idx,
          destType: dest.type,
          destIdx: dest.idx,
        })
      }
    }
    setSrc(null)
  }

  useEffect(() => {
    if (!src) return
    const inv = src.type === 'local' ? localInv : targetInv
    const current = inv.slots[src.idx]
    if (src.slot && (!current || current.id !== src.slot.id)) {
      setSrc(null)
    }
  }, [localInv, src, targetInv])

  return (
    <div
      className='interface'
      ref={containerRef}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      css={css`
        position: absolute;
        inset: 0;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        pointer-events: none;
        .interface-inner {
          width: ${ui.width}px;
          height: ${ui.height}px;
          background-image: url(${ui.bg});
          background-size: cover;
          transform-origin: bottom center;
          transform: scale(${scale});
          image-rendering: pixelated;
          pointer-events: none;
          touch-action: none;
        }
      `}
    >
      <div className='interface-inner'>
        {ui.localCells.map((cell, i) => (
          <InterfaceCell
            key={`l${i}`}
            slot={localInv.slots[cell.idx]}
            x={cell.x}
            y={cell.y}
            pointerCaptured={!!src}
            isDragging={src?.moving && src.type === 'local' && src.idx === cell.idx}
            isHighlighted={
              (cell.select && !src?.moving && src?.type === 'local' && src?.idx === cell.idx) || activeSlot === cell.idx
            }
            onPointerDown={handlePointerDown('local', cell.idx, cell, localInv.slots[cell.idx])}
          />
        ))}
        {ui.targetCells.map((cell, i) => (
          <InterfaceCell
            key={`t${i}`}
            slot={targetInv.slots[cell.idx]}
            x={cell.x}
            y={cell.y}
            pointerCaptured={!!src}
            isDragging={src?.moving && src.type === 'target' && src.idx === cell.idx}
            onPointerDown={handlePointerDown('target', cell.idx, cell, targetInv.slots[cell.idx])}
          />
        ))}
        {ui.extras.map((extra, idx) => (
          <InterfaceExtra
            key={idx}
            extra={extra}
            name={name}
            world={world}
            localInv={localInv}
            recipes={recipes}
            resonance={resonance}
            onGoHotbar={onGoHotbar}
            onGoEquipment={onGoEquipment}
            onGoChat={onGoChat}
            onTakeAll={onTakeAll}
            onCraft={onCraft}
            onCloseChat={onCloseChat}
          />
        ))}
        {src?.moving && src.slot && (
          <div
            css={css`
              position: absolute;
              width: 20px;
              height: 20px;
              left: ${cursorPos.x - 10}px;
              top: ${cursorPos.y - 10}px;
              background: url(${items[src.slot.id]?.icon});
              background-size: cover;
              pointer-events: none;
              image-rendering: pixelated;
            `}
          />
        )}
      </div>
    </div>
  )
}
